/**
 * ClusterBrowser - Semantic Cluster Navigation
 *
 * Browse discovered topic clusters from archive analysis.
 * Features:
 * - Cluster cards with keywords and descriptions
 * - Similar/dissimilar search from cluster embeddings
 * - Click a cluster to see its passages inline
 * - One-click draft generation from cluster passages
 *
 * @module @humanizer/studio/components/archive/ClusterBrowser
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useClusterApi, type ClusterSummary, type ClusterDetail as ClusterApiDetail } from '../../hooks/useClusterApi';
import { useBufferSync, type ContentItem } from '../../contexts/BufferSyncContext';
import useArchiveApi from '../../hooks/useArchiveApi';
import { SourceFilterPills, type FilterState } from '../ui/SourceFilterPills';

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

export interface ClusterBrowserProps {
  /** Archive ID to show clusters for */
  archiveId?: string;
  /** Called when a cluster is selected */
  onSelectCluster?: (clusterId: string) => void;
  /** Called when similar search is requested */
  onSearchSimilar?: (embedding: number[], sourceId: string) => void;
  /** Optional class name */
  className?: string;
}

/** Cluster data from clustering service */
export interface ClusterInfo {
  id: string;
  label: string;
  description: string;
  keywords: string[];
  passageCount: number;
  coherence: number; // 0-1
  dateRange?: {
    earliest: Date;
    latest: Date;
  };
  sourceDistribution?: Record<string, number>;
  embedding?: number[]; // Cluster centroid for similar search
  representativePassages?: Array<{
    id: string;
    text: string;
    similarity: number;
  }>;
}

/** Detail data fetched for a single cluster */
interface ClusterDetail {
  id: string;
  label: string;
  description: string;
  passages: Array<{ id: string; text: string; distanceFromCentroid?: number }>;
  totalPassages: number;
  coherence: number;
  keywords: string[];
}

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

/** Map ClusterSummary from hook to local ClusterInfo */
function mapClusterSummary(c: ClusterSummary): ClusterInfo {
  return {
    id: c.id,
    label: c.label,
    description: c.description || '',
    keywords: c.keywords || [],
    passageCount: c.nodeCount,
    coherence: c.quality,
  };
}

export function ClusterBrowser({
  archiveId,
  onSelectCluster,
  onSearchSimilar,
  className = '',
}: ClusterBrowserProps): React.ReactElement {
  const clusterApi = useClusterApi();
  const archiveApi = useArchiveApi();
  const { sessionId, setContent, commit, openNodeInContext } = useBufferSync();
  const [clusters, setClusters] = useState<ClusterInfo[]>([]);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDiscovering, setIsDiscovering] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Source type filter state (toggle mode via shared component)
  const [availableSources, setAvailableSources] = useState<string[]>([]);
  const [sourceFilters, setSourceFilters] = useState<FilterState>({});
  const selectedSources = useMemo(() => {
    const set = new Set<string>();
    for (const [k, v] of Object.entries(sourceFilters)) {
      if (v === 'include') set.add(k);
    }
    return set;
  }, [sourceFilters]);

  // Detail view state
  const [selectedClusterId, setSelectedClusterId] = useState<string | null>(null);
  const [detail, setDetail] = useState<ClusterDetail | null>(null);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [isDrafting, setIsDrafting] = useState(false);
  const [draftArc, setDraftArc] = useState<any>(null);

  // Load existing clusters (works on both local and cloud now)
  useEffect(() => {
    if (!clusterApi.isAvailable) return;
    setIsLoading(true);
    setError(null);
    clusterApi.listClusters()
      .then((res) => setClusters(res.map(mapClusterSummary)))
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load clusters'))
      .finally(() => setIsLoading(false));
  }, [clusterApi.isAvailable]);

  // Load available source types for filter chips
  useEffect(() => {
    if (!archiveApi.isAvailable) return;
    archiveApi.getSources()
      .then((res) => {
        const types = res.sources.map((s) => s.sourceType).filter(Boolean);
        setAvailableSources([...new Set(types)]);
      })
      .catch(() => { /* non-critical */ });
  }, [archiveApi.isAvailable, archiveApi.getSources]);

  // Source filter sources as SourceInfo array for shared component
  const sourceInfos = useMemo(
    () => availableSources.map(s => ({ sourceType: s })),
    [availableSources]
  );

  // Run cluster discovery
  const handleDiscover = useCallback(async () => {
    setIsDiscovering(true);
    setError(null);

    try {
      const res = await clusterApi.discoverClusters({
        minClusterSize: 5,
        maxClusters: 20,
        sourceTypes: selectedSources.size > 0 ? [...selectedSources] : undefined,
      });
      setClusters(res.clusters.map(mapClusterSummary));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Discovery failed');
    } finally {
      setIsDiscovering(false);
    }
  }, [clusterApi, selectedSources]);

  // Toggle cluster expansion
  const toggleExpand = useCallback((clusterId: string) => {
    setExpandedId((prev) => (prev === clusterId ? null : clusterId));
  }, []);

  // Handle cluster selection — open detail view
  const handleSelect = useCallback(
    async (cluster: ClusterInfo) => {
      setSelectedClusterId(cluster.id);
      setIsLoadingDetail(true);
      setDetail(null);
      setDraftArc(null);
      setError(null);

      try {
        const result: ClusterApiDetail = await clusterApi.getCluster(cluster.id);
        setDetail({
          id: result.id,
          label: result.label,
          description: result.description || '',
          passages: result.passages,
          totalPassages: result.totalPassages,
          coherence: result.coherence,
          keywords: result.keywords || [],
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load cluster');
      } finally {
        setIsLoadingDetail(false);
      }
    },
    [clusterApi]
  );

  // Back to list
  const handleBack = useCallback(() => {
    setSelectedClusterId(null);
    setDetail(null);
    setDraftArc(null);
    setError(null);
  }, []);

  // Generate draft from cluster
  const handleGenerateDraft = useCallback(async () => {
    if (!selectedClusterId || !detail) return;
    setIsDrafting(true);

    try {
      const result = await clusterApi.generateClusterDraft(selectedClusterId, {
        targetWordCount: 2500,
      });

      setDraftArc(result.arc);

      // Put draft into workspace buffer
      if (sessionId) {
        const draftItem: ContentItem = {
          id: `draft-cluster-${selectedClusterId}-${Date.now()}`,
          type: 'text',
          text: result.draft,
          metadata: {
            source: { type: 'cluster-draft', path: [detail.label] },
            timestamp: Date.now(),
            arc: result.arc,
            cullReport: result.cullReport,
            wordCount: result.wordCount,
          },
        };
        await setContent([draftItem]);
        await commit(`Draft: ${detail.label} (${result.wordCount} words, ${result.passageCount} passages)`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Draft generation failed');
    } finally {
      setIsDrafting(false);
    }
  }, [clusterApi, selectedClusterId, detail, sessionId, setContent, commit]);

  // Handle similar search from cluster centroid
  const handleSearchSimilar = useCallback(
    (cluster: ClusterInfo, e: React.MouseEvent) => {
      e.stopPropagation();
      if (cluster.embedding && onSearchSimilar) {
        onSearchSimilar(cluster.embedding, cluster.id);
      }
    },
    [onSearchSimilar]
  );

  // Get coherence color
  const getCoherenceColor = (coherence: number): string => {
    if (coherence >= 0.7) return 'var(--color-success)';
    if (coherence >= 0.4) return 'var(--color-warning)';
    return 'var(--studio-text-tertiary)';
  };

  // ─── UNAVAILABLE: Show setup message ───
  if (!clusterApi.isAvailable) {
    return (
      <div className={`cluster-browser ${className}`}>
        <div className="panel__empty">
          <div className="panel__empty-icon">🎯</div>
          <div className="panel__empty-title">Clusters</div>
          <div className="panel__empty-description">
            {clusterApi.unavailableReason || 'Clustering not available.'}
            {clusterApi.backend !== 'none' && (
              <>
                <br />
                <span className="panel__empty-badge">{clusterApi.backend}</span>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ─── DETAIL VIEW ───
  if (selectedClusterId) {
    return (
      <div className={`cluster-browser ${className}`}>
        <div className="cluster-detail">
          {/* Header */}
          <div className="cluster-detail__header">
            <button
              className="cluster-detail__back"
              onClick={handleBack}
              aria-label="Back to cluster list"
            >
              ← Back
            </button>
            {detail && (
              <>
                <h3 className="cluster-detail__title">{detail.label}</h3>
                <span
                  className="cluster-detail__coherence"
                  style={{ color: getCoherenceColor(detail.coherence) }}
                >
                  {Math.round(detail.coherence * 100)}%
                </span>
              </>
            )}
          </div>

          {/* Loading */}
          {isLoadingDetail && (
            <div className="panel__loading">
              <div className="panel__loading-spinner" />
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="panel__empty">
              <div className="panel__empty-icon">⚠️</div>
              <div className="panel__empty-title">Error</div>
              <div className="panel__empty-description">{error}</div>
            </div>
          )}

          {/* Arc outline (shown after draft generation) */}
          {draftArc && (
            <div className="cluster-detail__arc">
              <div className="cluster-detail__arc-title">Outline</div>
              {Array.isArray(draftArc.chapters) && draftArc.chapters.map((ch: any, i: number) => (
                <div key={i} className="cluster-detail__arc-chapter">
                  <span className="cluster-detail__arc-num">{i + 1}.</span>
                  <span>{ch.title || ch.name || `Chapter ${i + 1}`}</span>
                </div>
              ))}
            </div>
          )}

          {/* Passage list */}
          {detail && detail.passages.length > 0 && (
            <div className="cluster-detail__passages">
              {detail.passages.map((passage) => (
                <button
                  key={passage.id}
                  className="cluster-detail__passage cluster-detail__passage--clickable"
                  onClick={() => openNodeInContext({
                    nodeId: passage.id,
                    highlightText: passage.text.slice(0, 200),
                  })}
                >
                  <div className="cluster-detail__passage-text">{passage.text}</div>
                  {passage.distanceFromCentroid != null && (
                    <span className="cluster-detail__passage-distance">
                      {passage.distanceFromCentroid.toFixed(2)} dist
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}

          {/* Actions bar */}
          {detail && (
            <div className="cluster-detail__actions">
              <button
                className="archive-browser__action-btn"
                onClick={handleGenerateDraft}
                disabled={isDrafting || detail.passages.length === 0}
              >
                {isDrafting ? 'Generating...' : `Generate Draft (${detail.totalPassages} passages)`}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ─── LIST VIEW ───
  return (
    <div className={`cluster-browser ${className}`}>
      {/* Header with discover button */}
      <div className="search-results__header">
        <span className="search-results__count">
          {clusters.length} cluster{clusters.length !== 1 ? 's' : ''}
        </span>
        <button
          className="search-results__filter search-results__filter--active"
          onClick={handleDiscover}
          disabled={isDiscovering}
        >
          {isDiscovering ? 'Discovering...' : '🔍 Discover'}
        </button>
      </div>

      {/* Source type filter pills */}
      {availableSources.length > 1 && (
        <SourceFilterPills
          sources={sourceInfos}
          filters={sourceFilters}
          onFiltersChange={setSourceFilters}
          mode="toggle"
          showAll={false}
          showCounts={false}
          className=""
        />
      )}

      {/* Loading State */}
      {(isLoading || isDiscovering) && (
        <div className="panel__loading">
          <div className="panel__loading-spinner" />
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="panel__empty">
          <div className="panel__empty-icon">⚠️</div>
          <div className="panel__empty-title">Error</div>
          <div className="panel__empty-description">{error}</div>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !isDiscovering && !error && clusters.length === 0 && (
        <div className="panel__empty">
          <div className="panel__empty-icon">🎯</div>
          <div className="panel__empty-title">No Clusters Yet</div>
          <div className="panel__empty-description">
            Click "Discover" to find topic clusters in your archive
          </div>
        </div>
      )}

      {/* Cluster List */}
      {!isLoading && !isDiscovering && clusters.length > 0 && (
        <div className="cluster-list">
          {clusters.map((cluster) => (
            <div
              key={cluster.id}
              className="cluster-card"
              role="button"
              tabIndex={0}
              onClick={() => handleSelect(cluster)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSelect(cluster);
              }}
            >
              {/* Header */}
              <div className="cluster-card__header">
                <h3 className="cluster-card__title">{cluster.label}</h3>
                <span className="cluster-card__count">
                  {cluster.passageCount} items
                </span>
              </div>

              {/* Description — explains the latent-space anchor */}
              {cluster.description && (
                <p className="cluster-card__description">{cluster.description}</p>
              )}

              {/* Source distribution breakdown */}
              {cluster.sourceDistribution && Object.keys(cluster.sourceDistribution).length > 0 && (
                <div className="cluster-card__sources">
                  {Object.entries(cluster.sourceDistribution).map(([src, count]) => (
                    <span key={src} className="cluster-card__source-badge">
                      {src}: {count}
                    </span>
                  ))}
                </div>
              )}

              {/* Keywords */}
              <div className="cluster-card__keywords">
                {cluster.keywords.slice(0, 5).map((keyword, i) => (
                  <span key={i} className="cluster-card__keyword">
                    {keyword}
                  </span>
                ))}
                {cluster.keywords.length > 5 && (
                  <span className="cluster-card__keyword">
                    +{cluster.keywords.length - 5} more
                  </span>
                )}
              </div>

              {/* Coherence indicator */}
              <div
                className="search-result__meta"
                style={{ color: getCoherenceColor(cluster.coherence) }}
              >
                Coherence: {Math.round(cluster.coherence * 100)}%
                {cluster.dateRange && (
                  <span>
                    {' '}
                    · {new Date(cluster.dateRange.earliest).toLocaleDateString()} -{' '}
                    {new Date(cluster.dateRange.latest).toLocaleDateString()}
                  </span>
                )}
              </div>

              {/* Actions */}
              <div className="cluster-card__actions">
                <button
                  className="search-result__action search-result__action--similar"
                  onClick={(e) => handleSearchSimilar(cluster, e)}
                  title="Find content similar to this cluster"
                >
                  ↗️ Find Similar
                </button>
                <button
                  className="search-result__action"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleExpand(cluster.id);
                  }}
                  title={expandedId === cluster.id ? 'Hide samples' : 'Show samples'}
                >
                  {expandedId === cluster.id ? '▲ Hide' : '▼ Samples'}
                </button>
              </div>

              {/* Expanded representative passages */}
              {expandedId === cluster.id && cluster.representativePassages && (
                <div className="mt-sm">
                  {cluster.representativePassages.map((passage) => (
                    <div
                      key={passage.id}
                      className="search-result mt-xs search-result--no-border"
                    >
                      <div className="search-result__content">{passage.text}</div>
                      <div className="search-result__meta">
                        <span
                          className={`search-result__score ${
                            passage.similarity >= 0.7
                              ? 'search-result__score--high'
                              : passage.similarity >= 0.4
                                ? 'search-result__score--medium'
                                : 'search-result__score--low'
                          }`}
                        >
                          {Math.round(passage.similarity * 100)}% match
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default ClusterBrowser;
